import { useCallback } from "react"
import {
  FetchMethod,
  useCodeerApi,
  type UrlBuilderParams,
} from "../useCodeerApi"
import type { Response } from "@/types"

export type ClientAccessMode = "open" | "whitelist"

export interface ClientAccessResponse {
  mode: ClientAccessMode
}

export interface UpdateClientAccessRequest {
  mode: ClientAccessMode
}

export const useGetClientAccessApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientAccessResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-access`,
    FetchMethod.Get
  )
}

export const useUpdateClientAccessApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<ClientAccessResponse>,
    UpdateClientAccessRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-access`,
    FetchMethod.Patch
  )
}

export interface HumanHandoffSummaryItem {
  interaction_id: string
  client_user_id: string
  client_user_display_name: string | null
  external_user_id: string
  client_channel_type: string | null
  agent_id: string
  agent_name: string | null
  source: string
  handoff_at: string
  waiting_since: string
  waiting_minutes: number
  last_user_message_at: string | null
  last_platform_reply_at: string | null
  history_id: number | null
  page_no: number | null
}

export interface HumanHandoffSummary {
  needs_reply_count: number
  active_handoff_count: number
  manual_human_count: number
  waiting_over_30m_count: number
  top_waiting_items: HumanHandoffSummaryItem[]
}

export const useGetHumanHandoffSummaryApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<HumanHandoffSummary>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/human-handoffs/summary`,
    FetchMethod.Get
  )
}

export interface ClientUserUsage {
  conversations_7d: number
  histories_7d: number
}

export interface ClientUserMemoryEntry {
  id?: string
  content: string
  created_at?: string
  source?: string
  agent_id?: string
  actor_user_id?: string
}

export interface ClientUserTag {
  id: string
  name: string
  color: string
  client_user_count: number
  created_at: string
  updated_at: string
}

export interface ClientUserAssignedTag {
  id: string
  name: string
  color: string
}

export interface ClientUserListItem {
  id: string
  external_user_id: string
  email: string | null
  name: string | null
  alias: string | null
  display_name: string | null
  phone: string | null
  company: string | null
  job_title: string | null
  client_channel_type: string | null
  status: string
  join_source: string | null
  joined_at: string | null
  last_login_at: string | null
  last_conversation_at: string | null
  conversation_count: number
  human_agent_count: number
  human_manual_agent_count: number
  created_at: string
  updated_at: string
  tags: ClientUserAssignedTag[]
  usage: ClientUserUsage
}

export interface ClientUserHumanAgentSummary {
  agent_id: string
  agent_name: string | null
  interaction_mode_source: string | null
  human_mode_expires_at: string | null
}

export interface ClientUserDetail {
  id: string
  external_user_id: string
  email: string | null
  name: string | null
  alias: string | null
  display_name: string | null
  phone: string | null
  company: string | null
  job_title: string | null
  client_channel_type: string | null
  status: string
  provider_notes: string | null
  provider_notes_visibility?:
    | "visible"
    | "permission_denied"
    | "identity_masked"
  provider_links: string[]
  join_source: string | null
  joined_at: string | null
  last_login_at: string | null
  last_conversation_at: string | null
  conversation_count: number
  human_agent_count: number
  human_manual_agent_count: number
  human_agents: ClientUserHumanAgentSummary[]
  created_at: string
  updated_at: string
  tags: ClientUserAssignedTag[]
  memory_entries?: ClientUserMemoryEntry[]
}

export interface ClientUserFilters {
  q?: string
  status?: string
  channel_type?: string
  account_status?: string
  access_status?: string
  join_source?: string
  has_conversation?: boolean
  tag_id?: string
  limit?: number
  offset?: number
  order_by?: string
}

export const useListClientUsersApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserListItem[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users`,
    FetchMethod.Get
  )
}

export interface CreateClientUserTagRequest {
  name: string
  color?: string
}

export interface UpdateClientUserTagRequest {
  name?: string
  color?: string
}

export interface SetClientUserTagsRequest {
  tag_ids: string[]
}

export interface ClientUserTagUsageCountsRequest {
  tag_ids: string[]
}

export interface ClientUserTagUsageCount {
  tag_id: string
  client_user_count: number
}

export const useListClientUserTagsApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserTag[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-user-tags`,
    FetchMethod.Get
  )
}

export const useGetClientUserTagUsageCountsApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<ClientUserTagUsageCount[]>,
    ClientUserTagUsageCountsRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-user-tags/usage-counts`,
    FetchMethod.Post
  )
}

export const useCreateClientUserTagApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserTag>, CreateClientUserTagRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-user-tags`,
    FetchMethod.Post
  )
}

export const useUpdateClientUserTagApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserTag>, UpdateClientUserTagRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-user-tags/__placeholder__",
    FetchMethod.Patch,
    true,
    (params) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-user-tags/${params.tagId}`
  )
}

export const useDeleteClientUserTagApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<void>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-user-tags/__placeholder__",
    FetchMethod.Delete,
    true,
    (params) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-user-tags/${params.tagId}`
  )
}

export const useSetClientUserTagsApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserDetail>, SetClientUserTagsRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-users/__placeholder__/tags",
    FetchMethod.Put,
    true,
    (params) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${params.clientUserId}/tags`
  )
}

export interface ClientUserImportError {
  row: number
  value: string
  error: string
}

export interface ClientUserImportSummary {
  added: number
  skipped: number
  invalid: number
  errors: ClientUserImportError[]
}

export interface CreateClientUserRequest {
  external_user_id: string
  channel_type: string
  client_channel_id?: string
  email?: string
  name: string
  alias?: string
  phone?: string
  company?: string
  job_title?: string
  note?: string
}

export const useCreateClientUserApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserDetail>, CreateClientUserRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users`,
    FetchMethod.Post
  )
}

export interface ClientUserCsvPreviewRow {
  row: number
  external_user_id: string
  email: string | null
  name: string
  phone: string | null
  company: string | null
  job_title: string | null
  note: string | null
  ok: boolean
  error: string | null
}

export interface ClientUserCsvPreviewResponse {
  success: number
  failed: number
  rows: ClientUserCsvPreviewRow[]
}

export interface ClientUserCsvImportResult {
  success: number
  failed: number
  errors: ClientUserImportError[]
}

export const usePreviewImportClientUsersCsvApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserCsvPreviewResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/import`,
    FetchMethod.Post
  )
}

export const useConfirmImportClientUsersCsvApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserCsvImportResult>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/import`,
    FetchMethod.Post
  )
}

export const useImportClientUsersCsvApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientUserCsvImportResult>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/import`,
    FetchMethod.Post
  )
}

export interface UpdateClientUserStatusRequest {
  status: string
}

export const useUpdateClientUserStatusApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<void>, UpdateClientUserStatusRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}/status`,
    FetchMethod.Patch
  )
}

export const useGetClientUserApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<ClientUserDetail>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}`,
    FetchMethod.Get
  )
}

export interface UpdateClientUserRequest {
  name?: string
  alias?: string
  email?: string
  phone?: string
  company?: string
  job_title?: string
  status?: string
  provider_notes?: string
  provider_links?: string[]
  memory_entries?: ClientUserMemoryEntry[]
}

export const useUpdateClientUserApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<ClientUserDetail>, UpdateClientUserRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}`,
    FetchMethod.Patch
  )
}

export const useDeleteClientUserApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<void>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}`,
    FetchMethod.Delete
  )
}

export type BatchClientUsersAction = "delete" | "enable" | "disable"

export interface BatchClientUsersRequest {
  external_user_ids?: string[]
  client_user_ids?: string[]
  action: BatchClientUsersAction
}

export interface BatchClientUsersResponse {
  success: number
  not_found: number
}

export const useBatchClientUsersApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<BatchClientUsersResponse>,
    BatchClientUsersRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/batch`,
    FetchMethod.Post
  )
}

export interface ClientUserMetricsPoint {
  date: string
  conversations: number
  histories: number
}

export interface ClientUserMetricsResponse {
  total_conversations: number
  total_histories: number
  points: ClientUserMetricsPoint[]
}

export const useGetClientUserMetricsApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<ClientUserMetricsResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}/metrics`,
    FetchMethod.Get
  )
}

export interface ClientUserTopAgent {
  agent_id: string
  agent_name: string | null
  conversation_count: number
}

export interface ClientUserAgentInteraction {
  agent_id: string
  agent_name: string | null
  is_published: boolean
  handoff_enabled: boolean
  agent_response_mode: "ai" | "human_only"
  conversation_count: number
  last_conversation_at: string | null
  interaction_mode: string
  interaction_mode_source: string | null
  interaction_mode_changed_at: string | null
  human_mode_expires_at: string | null
}

export interface UpdateClientUserAgentInteractionRequest {
  interaction_mode: string
}

export const useGetClientUserTopAgentsApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<ClientUserTopAgent[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}/top-agents`,
    FetchMethod.Get
  )
}

export const useGetClientUserAgentInteractionsApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<Response<ClientUserAgentInteraction[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}/agent-interactions`,
    FetchMethod.Get
  )
}

export const useUpdateClientUserAgentInteractionApi = (
  organizationId: string,
  workspaceId: string,
  clientUserId: string
) => {
  return useCodeerApi<
    Response<ClientUserAgentInteraction>,
    UpdateClientUserAgentInteractionRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-users/__placeholder__/agent-interactions/__placeholder__",
    FetchMethod.Patch,
    true,
    (params) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/${clientUserId}/agent-interactions/${params.agentId}`
  )
}

export type ReplyModeAgentResponseMode = "ai" | "human_only"
export type ReplyModeTarget = "human" | "ai"
export type ReplyModeBatchItemStatus =
  | "pending"
  | "changed"
  | "already"
  | "failed"
export type ReplyModeBatchItemError =
  | "client_unavailable"
  | "agent_unavailable"
  | "unpublished"
  | "human_only"
  | "conflict"

export interface ReplyModeAgent {
  id: string
  name: string | null
  is_published: boolean
  agent_response_mode: ReplyModeAgentResponseMode
}

export interface ReplyModeBatchFilters {
  q?: string
  status?: string
  channel_type?: string
  account_status?: string
  access_status?: string
  join_source?: string
  has_conversation?: boolean
  tag_ids?: string[]
}

export interface CreateReplyModeBatchRequest {
  id: string
  agent_id: string
  client_user_ids?: string[]
  filters?: ReplyModeBatchFilters
}

export interface ReplyModeBatchItem {
  client_user_id: string
  status: ReplyModeBatchItemStatus
  // Server sends a safe error code; unknown codes are rendered generically.
  error: ReplyModeBatchItemError | string | null
}

export interface ReplyModeBatch {
  id: string
  agent_id: string
  agent_name: string | null
  is_published: boolean
  agent_response_mode: ReplyModeAgentResponseMode
  snapshot_at: string
  total: number
  changed: number
  already: number
  failed: number
  pending: number
  interaction_mode: ReplyModeTarget | null
  items: ReplyModeBatchItem[]
  next_cursor: string | null
}

export interface ExecuteReplyModeBatchRequest {
  interaction_mode: ReplyModeTarget
  retry?: boolean
}

export const useListReplyModeAgentsApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ReplyModeAgent[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/reply-mode-agents`,
    FetchMethod.Get
  )
}

export const useCreateReplyModeBatchApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ReplyModeBatch>, CreateReplyModeBatchRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/reply-mode-batches`,
    FetchMethod.Post
  )
}

export const useGetReplyModeBatchApi = (
  organizationId: string,
  workspaceId: string
) => {
  const urlBuilder = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/reply-mode-batches/${params.batchId}`,
    [organizationId, workspaceId]
  )
  return useCodeerApi<Response<ReplyModeBatch>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-users/reply-mode-batches/__placeholder__",
    FetchMethod.Get,
    true,
    urlBuilder
  )
}

export const useExecuteReplyModeBatchApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ReplyModeBatch>, ExecuteReplyModeBatchRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/client-users/reply-mode-batches/__placeholder__/execute",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/client-users/reply-mode-batches/${params.batchId}/execute`
  )
}
